import React from "react";
import { Box, Typography, Button, Chip, alpha, useTheme } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { formatProblemHtml } from "../../common/sanitizeProblemHtml";
import { problemStatementSx } from "../../common/problemStatementSx";

const ACCENT = "#137274";

const getYouTubeId = (url) => {
  if (!url) return null;
  const regExp =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^&\n]{11})/;
  const match = String(url).match(regExp);
  return match ? match[1] : null;
};

const ProblemExplanationTab = ({ videoLink, testCases = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const border = isDark ? "#30363d" : "rgba(15, 60, 70, 0.12)";
  const surface = isDark ? "#161b22" : "#ffffff";
  const muted = isDark ? "#8b949e" : "#5f6b73";

  const youtubeId = getYouTubeId(videoLink);
  const hasVideo = Boolean(youtubeId);

  const walkthroughs = testCases
    .map((tc, index) => ({ ...tc, sampleIndex: index + 1 }))
    .filter((tc) => String(tc.explanation || "").trim());

  const hasWalkthroughs = walkthroughs.length > 0;
  const isEmpty = !hasVideo && !hasWalkthroughs;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.35 }}>
          <OndemandVideoIcon sx={{ color: ACCENT, fontSize: 22 }} />
          <Typography
            sx={{ fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.02em" }}
          >
            Editorial
          </Typography>
          {hasVideo && (
            <Chip
              size="small"
              label="Video"
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.65rem",
                bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                color: ACCENT,
              }}
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ color: muted, fontSize: "0.82rem" }}>
          {hasVideo
            ? "Watch the walkthrough, then review sample notes below."
            : hasWalkthroughs
              ? "Written walkthrough for the sample cases."
              : "Video and walkthrough notes will appear here when available."}
        </Typography>
      </Box>

      {isEmpty && (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            px: 2,
            borderRadius: 2.5,
            border: `1px dashed ${border}`,
            bgcolor: isDark ? "#0d1117" : "#f8fafb",
          }}
        >
          <PlayCircleOutlineIcon
            sx={{ fontSize: 44, color: muted, mb: 1.5, opacity: 0.65 }}
          />
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            No explanation yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: muted, maxWidth: 300, mx: "auto" }}
          >
            Check the Description tab for the problem statement and sample test
            cases.
          </Typography>
        </Box>
      )}

      {hasVideo && (
        <Box
          sx={{
            mb: hasWalkthroughs ? 2.5 : 0,
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            overflow: "hidden",
            boxShadow: isDark ? "none" : "0 6px 20px rgba(15, 60, 70, 0.06)",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1.1,
              borderBottom: `1px solid ${border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
              Video walkthrough
            </Typography>
            <Button
              size="small"
              href={videoLink}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                color: ACCENT,
              }}
            >
              Open on YouTube
            </Button>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              paddingTop: "56.25%",
              bgcolor: isDark ? "#010409" : "#0f2a2b",
            }}
          >
            <Box
              component="iframe"
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title="Problem explanation video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </Box>
        </Box>
      )}

      {hasWalkthroughs && (
        <Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}
          >
            <MenuBookOutlinedIcon sx={{ color: ACCENT, fontSize: 20 }} />
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.92rem",
                letterSpacing: "-0.01em",
              }}
            >
              Sample walkthrough
            </Typography>
            <Chip
              size="small"
              label={`${walkthroughs.length} note${walkthroughs.length === 1 ? "" : "s"}`}
              sx={{
                height: 20,
                fontWeight: 700,
                fontSize: "0.65rem",
                bgcolor: alpha(muted, 0.12),
                color: muted,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {walkthroughs.map((sample) => (
              <Box
                key={sample.sampleIndex}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${border}`,
                  bgcolor: isDark ? "#0d1117" : "#fafbfc",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: ACCENT,
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                    display: "block",
                    mb: 1,
                  }}
                >
                  SAMPLE {sample.sampleIndex}
                </Typography>

                {(sample.input || sample.output) && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1,
                      mb: 1.25,
                    }}
                  >
                    {sample.input && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: muted,
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                          }}
                        >
                          Input
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 0.35,
                            p: 1,
                            borderRadius: 1.25,
                            bgcolor: isDark ? "#010409" : "#fff",
                            border: `1px solid ${border}`,
                            fontSize: "0.78rem",
                            overflow: "auto",
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          }}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: formatProblemHtml(sample.input),
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    {sample.output && (
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: muted,
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                          }}
                        >
                          Output
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 0.35,
                            p: 1,
                            borderRadius: 1.25,
                            bgcolor: isDark ? "#010409" : "#fff",
                            border: `1px solid ${border}`,
                            fontSize: "0.78rem",
                            overflow: "auto",
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          }}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: formatProblemHtml(sample.output),
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                <Typography
                  variant="body2"
                  sx={{
                    ...problemStatementSx(isDark),
                    fontSize: "0.88rem",
                    lineHeight: 1.65,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatProblemHtml(sample.explanation),
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProblemExplanationTab;
